/**
 * CATCHY ON THE HEVN PORTAL — the assistant in the corner of every page, built to look and work like
 * Catchy on the DAC dashboard (Max, 2026-10-14: "làm giống cái link DAC nhất có thể", with Catchy's own
 * bird, public/catchy.png). aiAssistant.js on the server says what it can read and why no more.
 *
 * WHAT CATCHY HAS, AND SO THIS DOES: the pill "Ask Catchy" launcher with the bird; a cream head with the
 * avatar, an "AI" tag and the four buttons (save the chat, new chat, expand, close); a greeting with
 * its time; suggested questions written by the AI from today's figures, hideable, with "Other
 * questions"; a screenshot pasted (Ctrl+V) or picked. One bird is enough (Max): it lives on the
 * launcher, where it bobs a little, and in the head of the panel.
 *
 * NO "ENERGY" BAR (Max, 2026-10-14: a bar that needs explaining is not earning its place, and the
 * "feeling great" mood was decoration copied from DAC). The daily question limit still holds; it is
 * said in one line only once ten or fewer questions are left, and the box is closed with a sentence
 * when none are.
 *
 * THE CONVERSATION SURVIVES A PAGE CHANGE: the Header — and this with it — is mounted by each screen,
 * so the chat lives in sessionStorage (this tab only), guarded because a private window can refuse
 * storage. A screenshot is kept only as a small thumbnail; the full image goes to the server once.
 *
 * THE ANSWER IS DRAWN AS REACT NODES, never HTML: "- " bullets, **bold**, and ids the Portal knows
 * (SKU-, PRJ-, SMP-, TSK-, OPA-) as links to their screens.
 *
 * z-index 58: above the page, BELOW every dialog (DecisionDialog is 60) — a corner button must never
 * be what is drawn over a dialog's own buttons.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enterSends } from './touchInput';
import { X, Download, Maximize2, Minimize2, ImagePlus, SendHorizontal, Sparkles, RefreshCw, ChevronUp, ChevronDown, SquarePen, MessagesSquare, ArrowLeft, ExternalLink, Eye } from 'lucide-react';
import { usePageItemNow } from './pageContext';
import { BACKEND_URL } from './backendConfig';
import useAuth from './useAuth';
import UserAvatar from './UserAvatar';
import { ProposalCard } from './CatchyCard';
import GapReport from './GapReport';

const KEY = 'hevnCatchyChat';
const MASCOT = `${process.env.PUBLIC_URL || ''}/catchy.png`;
/* THE BIRD IN ITS CAP — Max: "thêm cái mũ cho con catchy nhé". The cap is drawn here as an SVG
   rather than taken from the iStock picture he pointed at: that is a watermarked preview that needs a
   licence, and its checkerboard is painted into the JPG rather than being transparency. Placed in
   percent of the bird, so it sits on the head at any size (measured: the crown is at 37% across,
   16% down). */
/* also the face of Catchy's answers in AI Hub › Catchy Chat (Jay, 27/10: "Add catchy's icon") */
export const Mascot = ({ className = '' }) => (
  <span className={`ct-mascot ${className}`}>
    <img src={MASCOT} alt="" />
    <svg className="ct-cap" viewBox="0 0 64 50" aria-hidden="true">
      <path d="M17 19 L17 31 Q32 39 47 31 L47 19 Z" fill="#1F2430" />
      <path d="M17 28 Q32 36 47 28 L47 32 Q32 40 17 32 Z" fill="#F2B705" />
      <path d="M2 15 L32 26 L62 15 L62 18 L32 29 L2 18 Z" fill="#11151D" />
      <path className="ct-cap-top" d="M2 15 L32 4 L62 15 L32 26 Z" fill="#2E3444" />
      <circle cx="32" cy="15" r="2.2" fill="#F2B705" />
      <path d="M32 15 Q46 18 55 17.5" stroke="#F2B705" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M55 17.5 L55 31" stroke="#F2B705" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M52.3 30.5 L57.7 30.5 L59 40 L51 40 Z" fill="#F2B705" />
    </svg>
    {/* in the Christmas season the board makes way for a Santa hat (theme-christmas.css swaps them) */}
    <svg className="ct-santa" viewBox="0 0 64 50" aria-hidden="true">
      {/* the fur and the pompom carry a grey edge: white on the launcher's white was invisible, the hat read as a red hook */}
      <path d="M10 37 C13 20 25 7 41 7 C52 7 59 13 59 22 L52 25 C50 18 46 15.5 41.5 16.5 C34 18 30 27 31 37 Z" fill="#C8102E" />
      <path d="M41 7 C52 7 59 13 59 22 L55 23.5 C54 16 49 11.5 42 11 Z" fill="#9F0C24" />
      <circle cx="55.5" cy="26" r="5.4" fill="#F8FAFC" stroke="#94A3B8" strokeWidth="1.3" />
      <rect x="5" y="32" width="32" height="10" rx="5" fill="#F8FAFC" stroke="#94A3B8" strokeWidth="1.3" />
    </svg>
  </span>
);

const loadState = () => { try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; } };
const saveState = (v) => { try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* storage refused: memory only */ } };
const hhmm = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5); };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const hhmmDay = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2, '0')}/${MON[d.getMonth()]} ${d.toTimeString().slice(0, 5)}`; };

const ID_RE = /(SKU-\d{3,5}|PRJ-\d{3,5}|TSK-\d{3,5}|OPA-\d{6}|T?SMP-\d{4}(?:-[A-Za-z0-9]+)*)/g;
const linkFor = (id) => {
  if (/^SKU-|^PRJ-/.test(id)) return `/development/sample-tracker?tab=project&project=${id}`;
  if (/^TSK-/.test(id)) return `/development/sample-tracker?tab=tickets&ticket=${id}`;
  if (/^OPA-/.test(id)) return `/costing/opa-sct?docNo=${id}`;
  return `/development/sample-tracker?tab=sample&sample=${id}`;
};

const Inline = ({ text, go }) => String(text).split(/(\*\*[^*]+\*\*)/g).map((p, i) => {
  if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}><Inline text={p.slice(2, -2)} go={go} /></strong>;
  return p.split(ID_RE).map((q, j) => (j % 2
    ? <button key={`${i}-${j}`} type="button" className="ct-id" onClick={() => go(linkFor(q))}>{q}</button>
    : <React.Fragment key={`${i}-${j}`}>{q}</React.Fragment>));
});

const Answer = ({ text, go }) => {
  const lines = String(text || '').split('\n').filter((l) => l.trim() !== '');
  const out = []; let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={`u${out.length}`}>{list}</ul>); list = []; } };
  lines.forEach((l, i) => {
    const m = l.match(/^\s*[-*•]\s+(.*)$/);
    if (m) list.push(<li key={i}><Inline text={m[1]} go={go} /></li>);
    else { flush(); out.push(<p key={i}><Inline text={l.replace(/^#+\s*/, '')} go={go} /></p>); }
  });
  flush();
  return <>{out}</>;
};

/* A screenshot made small enough to send: longest side 1600px, JPEG — and a 240px thumbnail to show. */
export const shrink = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const draw = (max, q) => {
      const k = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
      g.drawImage(img, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', q);
    };
    const full = draw(1600, 0.85); const thumb = draw(240, 0.7);
    URL.revokeObjectURL(url);
    resolve({ mediaType: 'image/jpeg', data: full.split(',')[1], thumb });
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Not an image')); };
  img.src = url;
});

/* ONE CATCHY (Jay 29/10: "Catchy's floating window doesn't linked to this … can't find the history"): the server keeps
   every answered question as a turn of an AI Hub conversation and hands its number back (convPk), so the next question
   joins the same one and History opens it in the Hub. A new chat here is a new conversation there. */
export default function AskBubble({ canHub = false }) {
  const me = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  /* WHAT IS OPEN (Jay 30/10, pageContext.js): the screen's item travels with each question, shown above the box so the
     person sees what Catchy will take "this" to mean; the × leaves it out until another item opens. */
  const location = useLocation();
  const openItem = usePageItemNow();
  const [ctxOff, setCtxOff] = useState(null);
  const item = openItem && ctxOff !== openItem.n ? openItem : null;
  const itemText = item ? [item.id, item.version, item.label && item.label !== item.id ? item.label : ''].filter(Boolean).join(' · ') : '';
  const kept = loadState();
  const [open, setOpen] = useState(!!kept?.open);
  const [wide, setWide] = useState(!!kept?.wide);
  const [msgs, setMsgs] = useState(kept?.msgs || []);
  const [hello] = useState(kept?.hello || new Date().toISOString());
  const [showSugs, setShowSugs] = useState(kept?.showSugs ?? true);
  const [sugs, setSugs] = useState(kept?.sugs || null);
  const [variant, setVariant] = useState(kept?.variant || 0);
  const [energy, setEnergy] = useState(kept?.energy || null);
  const [convPk, setConvPk] = useState(kept?.convPk || null);
  /* the conversations list in place of the chat (Max 29/10: "user muốn mở lại conversation cũ ở đây thì làm như thế nào") */
  const [view, setView] = useState('chat');
  const [convs, setConvs] = useState(null);
  const [convErr, setConvErr] = useState('');
  const showConvs = async () => {
    setView('convs'); setConvErr('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/assistant/convs`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || t('xShell.failedStatus', { status: r.status }));
      setConvs(d.convs || []);
    } catch (e) { setConvErr(e.message); setConvs([]); }
  };
  const openConv = async (pk) => {
    setConvErr('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/assistant/convs/${pk}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || t('xShell.failedStatus', { status: r.status }));
      // a reminder Catchy started has no question (catchyWrite.js); a turn's proposal cards come back with it
      setMsgs(d.turns.flatMap((x) => [
        ...(x.started_by === 'catchy' ? [] : [{ role: 'user', text: x.question, at: x.asked_at || x.created_at }]),
        { role: 'assistant', text: x.answer, at: x.created_at, proposals: x.proposals || [] },
      ]));
      setConvPk(pk); setView('chat'); setText(''); setImage(null);
    } catch (e) { setConvErr(e.message); }
  };
  const newChat = () => { setMsgs([]); setText(''); setImage(null); setConvPk(null); setView('chat'); };
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sugBusy, setSugBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  // The suggested questions come in the portal's language (the server knows en, ko, vi, zh).
  const lang = ['ko', 'vi', 'zh'].find((c) => String(i18n.language || '').startsWith(c)) || 'en';

  useEffect(() => { saveState({ open, wide, msgs, hello, showSugs, sugs, variant, energy, convPk }); },
    [open, wide, msgs, hello, showSugs, sugs, variant, energy, convPk]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs, busy, open, showSugs]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const refreshEnergy = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/assistant/me`, { credentials: 'include' });
      if (r.ok) setEnergy(await r.json());
    } catch (e) { /* the bar keeps its last reading */ }
  }, []);
  const loadSugs = useCallback(async (v) => {
    setSugBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/assistant/suggest?lang=${lang}&variant=${v}`, { credentials: 'include' });
      const d = await r.json();
      if (Array.isArray(d.questions) && d.questions.length) setSugs({ lang, list: d.questions });
    } catch (e) { /* the fixed suggestions stay */ } finally { setSugBusy(false); }
  }, [lang]);
  useEffect(() => {
    if (!open) return;
    refreshEnergy();
    if (!sugs || sugs.lang !== lang) loadSugs(variant);
  }, [open, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (path) => navigate(path);

  /* "DEEPER ANALYSIS" (Jay task #1181): the question behind answer `i` once more on the stronger model, only after the person has read
     what it costs; the new answer comes under the conversation, marked. `deepAsk` = { index, left, limit, admin } while the warning shows. */
  const [deepAsk, setDeepAsk] = useState(null);
  const offerDeep = async (index) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/assistant/deep-left`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      setDeepAsk({ index, left: d.left, limit: d.limit, admin: !!d.admin });
    } catch (e) { setDeepAsk({ index, left: null, limit: null, admin: false }); }
  };
  const runDeep = async () => {
    const index = deepAsk?.index; setDeepAsk(null);
    if (index == null || busy) return;
    const upTo = msgs.slice(0, index).filter((m) => !m.error);
    setBusy(true);
    try {
      const body = upTo.map(({ role, text: tx }) => ({ role, text: tx || '' }));
      if (body.length && body[0].role !== 'user') body.unshift({ role: 'user', text: '(Catchy wrote first — a reminder)' });
      const r = await fetch(`${BACKEND_URL}/api/assistant/ask`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: body, convPk, deep: true, page: { path: `${location.pathname}${location.search}`.slice(0, 200) } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xShell.failedStatus', { status: r.status }));
      setMsgs((all) => [...all, { role: 'assistant', text: d.answer, at: new Date().toISOString(), deep: true, askPk: d.askPk || null,
        gap: d.gap || null, unverified: d.unverified || [], lookups: d.lookups || [] }]);
    } catch (e) {
      setMsgs((all) => [...all, { role: 'assistant', text: e.message, error: true, at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const ask = async (q) => {
    const question = String(q ?? text).trim();
    if ((!question && !image) || busy) return;
    const now = new Date().toISOString();
    const mine = { role: 'user', text: question, at: now, ...(image ? { thumb: image.thumb } : {}) };
    const next = [...msgs, mine];
    const sendImage = image;
    setMsgs(next); setText(''); setImage(null); setBusy(true); setDeepAsk(null);
    if (inputRef.current) { inputRef.current.style.height = ''; inputRef.current.style.overflowY = ''; }
    try {
      /* what became of a proposal card travels with its answer, so "and the date too" after a Confirm reads the record as it now is */
      const body = next.filter((m) => !m.error).map(({ role, text: tx, proposals }) => ({ role,
        text: `${tx || ''}${(proposals || []).length ? `\n${proposals.map((c) => `[card #${c.id} ${c.entityId || c.title}: ${c.status}]`).join(' ')}` : ''}` }));
      // a conversation Catchy started opens with its reminder — the model needs a turn of the person's before it
      if (body.length && body[0].role !== 'user') body.unshift({ role: 'user', text: '(Catchy wrote first — a reminder)' });
      if (sendImage) body[body.length - 1].image = { mediaType: sendImage.mediaType, data: sendImage.data };
      const r = await fetch(`${BACKEND_URL}/api/assistant/ask`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: body, convPk,
          page: { path: `${location.pathname}${location.search}`.slice(0, 200), item: item ? { kind: item.kind, id: item.id, version: item.version || '', label: item.label || '', screen: item.screen || '' } : null } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xShell.failedStatus', { status: r.status }));
      setMsgs([...next, { role: 'assistant', text: d.answer, at: new Date().toISOString(), proposals: d.proposals || [],
        askPk: d.askPk || null, gap: d.gap || null, unverified: d.unverified || [], lookups: d.lookups || [], deepOffer: !!d.deepOffer }]);
      if (d.convPk) setConvPk(d.convPk);
      if (typeof d.asksLeft === 'number') setEnergy({ left: d.asksLeft, limit: d.limit || energy?.limit || 60 });
    } catch (e) {
      setMsgs([...next, { role: 'assistant', text: e.message, error: true, at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const attach = async (file) => {
    if (!file || !/^image\//.test(file.type)) return;
    try { setImage(await shrink(file)); } catch (e) { /* not an image: ignore */ }
  };
  const onPaste = (e) => {
    const f = [...(e.clipboardData?.items || [])].find((it) => it.kind === 'file' && /^image\//.test(it.type));
    if (f) { e.preventDefault(); attach(f.getAsFile()); }
  };

  const saveChat = () => {
    const lines = [`Catchy — HEVN Portal`, new Date().toLocaleString(), ''];
    msgs.forEach((m) => lines.push(`[${hhmm(m.at)}] ${m.role === 'user' ? t('ask.you') : 'Catchy'}: ${m.text || (m.thumb ? t('xShell.chatScreenshot') : '')}`, ''));
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const d = new Date(); const z = (n) => String(n).padStart(2, '0');
    // The reader's own clock in the file name, not UTC (the first build named a 14:17 chat "0717").
    a.download = `catchy-${d.getFullYear()}${z(d.getMonth() + 1)}${z(d.getDate())}-${z(d.getHours())}${z(d.getMinutes())}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  // Quiet until it matters: the limit is said only when it is close, never as a standing gauge.
  const lowLeft = energy && typeof energy.left === 'number' && energy.left <= 10 ? energy.left : null;
  const fixed = [t('ask.s1'), t('ask.s2'), t('ask.s3')];
  const list = sugs?.lang === lang && sugs.list?.length ? sugs.list : fixed;
  const fromAi = sugs?.lang === lang && sugs.list?.length;

  /* MOVED BY ITS HEAD (Anh Le's feedback FB-0001, 03/11: "Can the Catchy window be moved during a chat? … it ends up blocking my
     view"). Drag the head (not its buttons) anywhere on the screen; the place is kept in this browser; a double-click on the head
     puts it back in its corner. Not on a phone, where it fills the screen (#1172). */
  const panelRef = useRef(null);
  const [pos, setPos] = useState(() => { try { const v = JSON.parse(localStorage.getItem('catchyPanelPos') || 'null'); return v && Number.isFinite(v.x) ? v : null; } catch (e) { return null; } });
  const fit = (p) => {
    const el = panelRef.current; if (!p || !el) return p;
    const w = el.offsetWidth; const h = el.offsetHeight;
    return { x: Math.min(Math.max(p.x, 0), Math.max(0, window.innerWidth - w)), y: Math.min(Math.max(p.y, 0), Math.max(0, window.innerHeight - Math.min(h, 120))) };
  };
  const dragStart = (e) => {
    if (window.innerWidth <= 600 || e.button !== 0 || e.target.closest('button, a, input, textarea, select')) return;
    const el = panelRef.current; if (!el) return;
    const r = el.getBoundingClientRect(); const dx = e.clientX - r.left; const dy = e.clientY - r.top;
    e.preventDefault();
    const move = (ev) => setPos(fit({ x: ev.clientX - dx, y: ev.clientY - dy }));
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      setPos((cur) => { try { if (cur) localStorage.setItem('catchyPanelPos', JSON.stringify(cur)); } catch (er) { /* private window */ } return cur; });
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const dragReset = (e) => { if (e.target.closest('button')) return; setPos(null); try { localStorage.removeItem('catchyPanelPos'); } catch (er) { /* ignore */ } };
  // a window made wider or a smaller screen: keep it on the screen
  useEffect(() => { if (open && pos) setPos((cur) => fit(cur)); }, [open, wide]); // eslint-disable-line react-hooks/exhaustive-deps
  const placed = pos && typeof window !== 'undefined' && window.innerWidth > 600 ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined;

  return (
    <>
      {open && (
        <div ref={panelRef} className={`ct-panel${wide ? ' wide' : ''}${placed ? ' moved' : ''}`} style={placed} role="dialog" aria-label="Catchy" data-ct-panel>
          <div className="ct-head" onPointerDown={dragStart} onDoubleClick={dragReset} title={t('ask.moveHint')} data-ct-drag>
            <Mascot className="ct-avatar" />
            <div className="ct-who">
              <div className="ct-name">Catchy <span className="ct-tag"><Sparkles size={11} /> AI · Claude</span></div>
              <div className="ct-role">{t('ask.role')}</div>
              <div className="ct-note">{t('ask.note')}</div>
            </div>
            <div className="ct-tools">
              <button type="button" title={t('ask.save')} aria-label={t('ask.save')} disabled={!msgs.length} onClick={saveChat}><Download size={16} /></button>
              <button type="button" title={t('xAiHub.bubbleConvs')} aria-label={t('xAiHub.bubbleConvs')} data-ct-convs="1"
                className={view === 'convs' ? 'on' : ''} onClick={() => (view === 'convs' ? setView('chat') : showConvs())}><MessagesSquare size={16} /></button>
              <button type="button" title={t('ask.newChat')} aria-label={t('ask.newChat')} data-ct-new="1" onClick={newChat}><SquarePen size={16} /></button>
              <button type="button" title={wide ? t('ask.shrink') : t('ask.expand')} aria-label={wide ? t('ask.shrink') : t('ask.expand')} onClick={() => setWide((v) => !v)}>
                {wide ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button type="button" title={t('ask.close')} aria-label={t('ask.close')} onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
          </div>

          {view === 'convs' && (
            <div className="ct-convs" data-ct-convlist="1">
              <div className="ct-convs-head">
                <button type="button" className="ct-link" onClick={() => setView('chat')}><ArrowLeft size={13} /> {t('xAiHub.bubbleBack')}</button>
                <b>{t('xAiHub.bubbleConvs')}</b>
              </div>
              {convErr && <p className="ct-convs-empty">{convErr}</p>}
              {convs === null && <p className="ct-convs-empty">…</p>}
              {convs && !convs.length && !convErr && <p className="ct-convs-empty">{t('xAiHub.noConvs')}</p>}
              <div className="ct-convs-list">
                {(convs || []).map((c) => (
                  <button key={c.conv_pk} type="button" className={`ct-conv${c.conv_pk === convPk ? ' on' : ''}`} data-ct-conv={c.conv_pk} onClick={() => openConv(c.conv_pk)}>
                    <b>{c.title}</b>
                    <span>{hhmmDay(c.updated_at)} · {t('xAiHub.turns', { n: c.turns })} · {c.source === 'bubble' ? t('xAiHub.fromBubble') : t('xAiHub.fromHub')}</span>
                  </button>
                ))}
              </div>
              {canHub && (
                <button type="button" className="ct-link ct-convs-hub" data-ct-hub="1"
                  onClick={() => { setOpen(false); navigate(convPk ? `/ai-hub?tab=ask&conv=${convPk}` : '/ai-hub?tab=ask'); }}>
                  <ExternalLink size={12} /> {t('xAiHub.openInHub')}
                </button>
              )}
            </div>
          )}
          {view === 'chat' && (<>
          <div className="ct-list" ref={listRef}>
            <div className="ct-msg assistant">
              <p>{t('ask.hello')}</p>
              <span className="ct-time">{hhmm(hello)}</span>
            </div>
            {msgs.map((m, i) => {
              const bubble = (
                <div key={i} className={`ct-msg ${m.role}${m.error ? ' err' : ''}`}>
                  {m.thumb && <img className="ct-thumb" src={m.thumb} alt={t('ask.screenshot')} />}
                  {m.deep && <span className="ct-deep-tag" data-ct-deeptag>{t('xCatchy.deepTag')}</span>}
                  {m.role === 'assistant' && !m.error ? <Answer text={m.text} go={go} /> : (m.text ? <p>{m.text}</p> : null)}
                  {(m.proposals || []).map((c) => (
                    <ProposalCard key={c.id} card={c} onChange={(nc) => setMsgs((all) => all.map((x, k) => (k === i ? { ...x, proposals: x.proposals.map((y) => (y.id === nc.id ? nc : y)) } : x)))} />
                  ))}
                  {/* what blocked the answer, and a button to tell the admin (Max 31/10) */}
                  {m.role === 'assistant' && !m.error && (
                    <GapReport askPk={m.askPk} gap={m.gap} unverified={m.unverified || []} lookups={m.lookups || []} gaveUp={String(m.text || '').startsWith('I could not finish looking this up')} />
                  )}
                  {/* the stronger model, on request, after the cost is shown (Jay task #1181) */}
                  {m.role === 'assistant' && !m.error && m.deepOffer && !m.deep && (
                    deepAsk && deepAsk.index === i ? (
                      <div className="ct-deep-warn" role="alertdialog" data-ct-deepwarn>
                        <p>{t('xCatchy.deepWarn')}</p>
                        <p className="ct-deep-left">{deepAsk.admin ? t('xCatchy.deepAdmin') : t('xCatchy.deepLeft', { n: deepAsk.left ?? '?', limit: deepAsk.limit ?? '?' })}</p>
                        <div className="ct-deep-btns">
                          <button type="button" className="ct-deep-go" disabled={busy || (!deepAsk.admin && !deepAsk.left)} onClick={runDeep} data-ct-deepgo>{t('xCatchy.deepRun')}</button>
                          <button type="button" className="ct-deep-no" onClick={() => setDeepAsk(null)}>{t('xCatchy.deepCancel')}</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="ct-deep-btn" disabled={busy} onClick={() => offerDeep(i)} data-ct-deep={i}><Sparkles size={13} /> {t('xCatchy.deepBtn')}</button>
                    )
                  )}
                  <span className="ct-time">{hhmm(m.at)}</span>
                </div>
              );
              /* the person's own face beside what they wrote (Jay, 27/10) */
              return m.role === 'user'
                ? <div key={i} className="ct-mine">{bubble}<UserAvatar url={me.avatarUrl} name={me.name} size={28} /></div>
                : bubble;
            })}
            {busy && <div className="ct-msg assistant ct-typing"><span /><span /><span /></div>}
          </div>

          <div className="ct-sugs">
            <div className="ct-sugs-head">
              <button type="button" className="ct-link" onClick={() => setShowSugs((v) => !v)}>
                {showSugs ? t('ask.hideSugs') : t('ask.showSugs')} {showSugs ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              <span style={{ flex: 1 }} />
              {fromAi && <span className="ct-tag"><Sparkles size={11} /> AI</span>}
              <button type="button" className="ct-link" disabled={sugBusy}
                onClick={() => { const v = variant + 1; setVariant(v); loadSugs(v); setShowSugs(true); }}>
                <RefreshCw size={12} className={sugBusy ? 'ct-spin' : ''} /> {t('ask.otherSugs')}
              </button>
            </div>
            {showSugs && (
              <div className="ct-sug-list">
                {list.map((s) => <button key={s} type="button" className="ct-sug" disabled={busy} onClick={() => ask(s)}>{s}</button>)}
              </div>
            )}
          </div>

          {item && (
            <div className="ct-context" title={t('ask.lookingAtHint')} data-ct-context={item.id}>
              <Eye size={13} />
              <span><b>{t('ask.lookingAt')}</b> {item.screen ? `${item.screen} · ` : ''}{itemText}</span>
              <button type="button" aria-label={t('ask.remove')} title={t('ask.dropContext')} onClick={() => setCtxOff(openItem.n)}><X size={13} /></button>
            </div>
          )}
          {image && (
            <div className="ct-attached">
              <img src={image.thumb} alt={t('ask.screenshot')} />
              <span>{t('ask.screenshotReady')}</span>
              <button type="button" aria-label={t('ask.remove')} title={t('ask.remove')} onClick={() => setImage(null)}><X size={14} /></button>
            </div>
          )}
          <div className="ct-input">
            <button type="button" className="ct-img" title={t('ask.attach')} aria-label={t('ask.attach')} onClick={() => fileRef.current?.click()}>
              <ImagePlus size={18} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { attach(e.target.files?.[0]); e.target.value = ''; }} />
            <textarea ref={inputRef} rows={1} value={text} maxLength={1500} placeholder={t('ask.placeholder')}
              onChange={(e) => {
                setText(e.target.value);
                /* One line until the question needs more: the box grows with it up to 110px, then scrolls. */
                const el = e.target; el.style.height = 'auto';
                el.style.height = `${Math.min(Math.max(el.scrollHeight + 4, 44), 110)}px`;
                el.style.overflowY = el.scrollHeight + 4 > 110 ? 'auto' : 'hidden';
              }} onPaste={onPaste}
              onKeyDown={(e) => { if (enterSends(e)) { e.preventDefault(); ask(); } }} />
            <button type="button" className="ct-send" disabled={busy || (!text.trim() && !image) || energy?.left === 0}
              onClick={() => ask()} title={t('ask.send')} aria-label={t('ask.send')}>
              <SendHorizontal size={18} />
            </button>
          </div>

          {lowLeft != null && (
            <div className={`ct-left${lowLeft === 0 ? ' none' : ''}`}>
              {lowLeft === 0 ? t('ask.noneLeft') : t('ask.leftToday', { count: lowLeft })}
            </div>
          )}
          </>)}
        </div>
      )}
      <button type="button" className={`ct-launch${open ? ' open' : ''}`} onClick={() => setOpen((v) => !v)}
        aria-expanded={open} aria-label={t('ask.launch')}>
        <Mascot className="ct-launch-bird" />
        <span>{t('ask.launch')}</span>
      </button>
    </>
  );
}
